import adminRoutes from "../../routes/adminSidebar";
import companyRoutes from "../../routes/companySidebar";
import agentRoutes from "../../routes/agentSidebar";
import fyrstuplogo from "../../assets/Fyrst_Up_Logo.png";
import {
	NavLink,
	useLocation,
	useNavigate,
	useNavigation,
	useParams,
} from "react-router-dom";
import * as Icons from "../../icons";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useSidebar } from "../../context/SidebarContext";
import { useQuery } from "@apollo/client";
import { GET_COMPANY_DETAILS } from "../../graphql/companyQueries";
// import SidebarSubmenu from './SidebarSubmenu'

const Icon = ({ icon, ...props }) => {
	const Icon = Icons[icon];
	return <Icon {...props} />;
};

// Keep only items allowed by FUB; drop sections left empty.
const filterFubGroups = (groups, isFubEnabled) =>
	groups
		.map((group) => ({
			...group,
			items: group.items.filter((route) => !route.requiresFub || isFubEnabled),
		}))
		.filter((group) => group.items.length > 0);

const SECTION_HEADER_CLS =
	"px-6 pt-4 pb-1 text-base font-bold uppercase tracking-wide text-gray-500";

const SidebarContent = () => {
	const { collapsed, toggleCollapsed } = useSidebar();
	const location = useLocation();
	const { companyId } = useParams();
	const [routes, setRoutes] = useState([]);
	const { logout } = useContext(AuthContext);
	const navigate = useNavigate();
	const user = JSON.parse(localStorage.getItem("userInfo"));
	const { pathname } = location;
	const splitLocation = pathname.split("/");

	// Per-section expand/collapse (vertical), persisted across reloads.
	const [openSections, setOpenSections] = useState(() => {
		try {
			return JSON.parse(localStorage.getItem("sidebarOpenSections") || "{}");
		} catch {
			return {};
		}
	});

	const isSectionOpen = (title) => openSections[title] !== false;

	const toggleSection = (title) => {
		setOpenSections((prev) => {
			const next = { ...prev, [title]: prev[title] === false ? true : false };
			localStorage.setItem("sidebarOpenSections", JSON.stringify(next));
			return next;
		});
	};

	// Fetch company data to check isFubEnabled
	// Use network-only to always get fresh data (in case SUPER_ADMIN toggles FUB)
	const { data: companyData } = useQuery(GET_COMPANY_DETAILS, {
		variables: { companyId },
		skip: !companyId || location.pathname.includes("/administrator"),
		fetchPolicy: "network-only",
	});

	const handleLogout = () => {
		logout();
		navigate("/");
	};

	useEffect(() => {
		if (location.pathname.includes("/administrator")) {
			setRoutes(adminRoutes);
		} else if (user?.role?.name === "AGENT") {
			const isFubEnabled = companyData?.getCompanyById?.isFubEnabled;
			setRoutes(filterFubGroups(agentRoutes, isFubEnabled));
		} else {
			const isFubEnabled = companyData?.getCompanyById?.isFubEnabled;
			setRoutes(filterFubGroups(companyRoutes, isFubEnabled));
		}
	}, [location, user?.role?.name, companyData]);

	const logoutIcon = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.5}
			stroke="currentColor"
			className="w-6 h-6"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
			/>
		</svg>
	);

	const renderItem = (route) => {
		const fullPath = location.pathname.includes("/administrator/")
			? route.path
			: `/company/${companyId}${route.path}`;
		const isActive =
			location.pathname === fullPath ||
			(route.path !== "/dashboard" &&
				route.path !== "/agent/dashboard" &&
				location.pathname.startsWith(fullPath));

		return (
			<li
				className={`${
					isActive ? "bg-primary text-white" : "text-secondary"
				} relative ${collapsed ? "px-0 py-2.5" : "px-6 py-2.5"} hover:opacity-80 `}
				key={route.name}
			>
				<NavLink
					to={fullPath}
					end={route.path === "/agent/dashboard" || route.path === "/dashboard"}
					title={collapsed ? route.name : undefined}
					className={`inline-flex items-center w-full text-sm font-semibold transition-colors duration-150 hover:text-white-800 ${
						collapsed ? "justify-center" : ""
					}`}
				>
					<span>{route.icon}</span>
					{!collapsed && <span className={` ml-4 text-base`}>{route.name}</span>}
				</NavLink>
			</li>
		);
	};

	return (
		<div className="">
			{/* Logo */}
			<div className="border-b border-gray-100 px-4 py-2">
				<button
					type="button"
					onClick={toggleCollapsed}
					title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
					aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
					className="flex w-full items-center justify-center"
				>
					{collapsed ? (
						<img
							className="h-7 w-7 object-contain"
							src={fyrstuplogo}
							alt="Fyrst Up"
						/>
					) : (
						<img
							className="h-8 w-auto object-contain"
							src={fyrstuplogo}
							alt="Fyrst Up"
						/>
					)}
				</button>
			</div>

			<ul className="mt-2">
				{routes.length > 0 ? (
					routes.map((group, idx) => {
						// Icon-rail (collapsed): show all icons; divider before labelled groups.
						if (collapsed) {
							return (
								<li key={group.section ?? idx} className="list-none">
									{group.section && (
										<div className="mx-4 my-2 border-t border-gray-200" />
									)}
									<ul>{group.items.map((route) => renderItem(route))}</ul>
								</li>
							);
						}
						// Expanded: collapsible section header (null section = Dashboard, no header).
						const open = group.section ? isSectionOpen(group.section) : true;
						return (
							<li key={group.section ?? idx} className="list-none">
								{group.section && (
									<>
										<div className="mx-6 mt-2 border-t border-gray-100" />
										<button
											type="button"
											onClick={() => toggleSection(group.section)}
											className={`${SECTION_HEADER_CLS} flex w-full items-center justify-between hover:text-secondary`}
										>
											<span>{group.section}</span>
											<svg
												className={`h-5 w-5 shrink-0 transition-transform duration-200 ${
													open ? "" : "-rotate-90"
												}`}
												fill="none"
												viewBox="0 0 24 24"
												strokeWidth={2.5}
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M19.5 8.25l-7.5 7.5-7.5-7.5"
												/>
											</svg>
										</button>
									</>
								)}
								{open && (
									<ul>{group.items.map((route) => renderItem(route))}</ul>
								)}
							</li>
						);
					})
				) : (
					<div></div>
				)}
			</ul>

			{collapsed ? (
				<div className="px-0 py-1">
					<div className="mx-4 my-2 border-t border-gray-200" />
					<button
						type="button"
						onClick={handleLogout}
						title="Logout"
						className="relative flex w-full items-center justify-center px-0 py-2.5 text-secondary hover:opacity-80"
					>
						<span>{logoutIcon}</span>
					</button>
				</div>
			) : (
				<div>
					<div className="mx-6 mt-2 border-t border-gray-100" />
					<button
						type="button"
						onClick={() => toggleSection("Account")}
						className={`${SECTION_HEADER_CLS} flex w-full items-center justify-between hover:text-secondary`}
					>
						<span>Account</span>
						<svg
							className={`h-5 w-5 shrink-0 transition-transform duration-200 ${
								isSectionOpen("Account") ? "" : "-rotate-90"
							}`}
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={2.5}
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M19.5 8.25l-7.5 7.5-7.5-7.5"
							/>
						</svg>
					</button>
					{isSectionOpen("Account") && (
						<button
							type="button"
							onClick={handleLogout}
							className="relative flex w-full items-center px-6 py-2.5 text-secondary text-sm font-semibold hover:opacity-80"
						>
							<span>{logoutIcon}</span>
							<span className="ml-4 text-base">Logout</span>
						</button>
					)}
				</div>
			)}
		</div>
	);
};

export default SidebarContent;
